// @ts-nocheck
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";

/* Store Custom Code - Start */
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";
/* Store Custom Code - End */

const Date = getWidgetComponent("OrderDetails/OrderDate");
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");
const Salesperson = getWidgetComponent("OrderDetails/OrderSalesperson");
const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const PaymentTerms = getWidgetComponent("OrderDetails/OrderDetailsPaymentTerms");
const PoNumber = getWidgetComponent("OrderDetails/OrderPo");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");
const BillingAddress = getWidgetComponent("OrderDetails/OrderDetailsBillingAddress");
const ShippingMethod = getWidgetComponent("OrderDetails/OrderDetailsShippingMethod");
const ShipmentPackages = getWidgetComponent("OrderDetails/OrderDetailsShipmentPackages");
const VatNumber = getWidgetComponent("OrderDetails/OrderDetailsVatNumber");
const VmiLocation = getWidgetComponent("OrderDetails/OrderDetailsVmiLocation");

interface Props extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
}

export interface OrderDetailsInformationStyles {
    block?: BlockProps;
    orderNumberHeading?: TypographyProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    billingInformationTitle?: TypographyProps;
    // Store Custom Code
    adressesGridContainer?: GridContainerProps;
    adressesGridItem?: GridItemProps;
    shippingGridItem?: GridItemProps;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    /* Store Custom Code - Start */
    block: {
        ...orderProps.block,
        variantProps: {
            separated: {
                adaptToContext: true,
                border: {
                    width: pxToRem(1),
                },
            },
        },
    },
    /* Store Custom Code - End */
    orderNumberHeading: orderProps.subtitle,
    infoGridContainer: infoProps.grid?.container,
    infoGridItem: infoProps.grid?.item,
    notesGridItem: infoProps.grid?.largeItem,
    shippingInformationTitle: orderProps.subtitle,
    billingInformationTitle: orderProps.subtitle,
    /* Store Custom Code - Start */
    adressesGridContainer: infoProps.grid?.container,
    adressesGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    shippingGridItem: {
        width: 12,
    },
    infoLabel: infoProps.label,
    infoValue: infoProps.value,
    /* Store Custom Code - End */
};

const styles = orderDetailsInformationStyles;

const OrderDetailsInformation: FC<Props> = ({ orderState: baseOrderState }) => {
    const { orderSettings, productSettings } = useSettingsCollection();
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    const order = orderState.value;

    if (!order) {
        return null;
    }

    const showPoNumber = orderSettings.showPoNumber && order.customerPO;
    const showVatNumber = productSettings.enableVat && order.customerVatNumber;
    const showTerms = orderSettings.showTermsCode && order.terms;
    const showInformations =
        order.orderDate ||
        showPoNumber ||
        order.statusDisplay ||
        order.salesperson ||
        showVatNumber ||
        showTerms ||
        order.vmiLocationName;

    return (
        <OrderStateContext.Provider value={orderState}>
            <Block {...styles.block}>
                <Typography {...styles.orderNumberHeading}>
                    {translate("Order #")}
                    {order.erpOrderNumber ? order.erpOrderNumber : order.webOrderNumber}
                </Typography>
                {showInformations ? (
                    <GridContainer {...styles.infoGridContainer}>
                        {order.orderDate ? (
                            <GridItem {...styles.infoGridItem}>
                                <Date />
                            </GridItem>
                        ) : null}
                        {showPoNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <PoNumber />
                            </GridItem>
                        ) : null}
                        {order.statusDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <Status />
                            </GridItem>
                        ) : null}
                        {order.salesperson ? (
                            <GridItem {...styles.infoGridItem}>
                                <Salesperson />
                            </GridItem>
                        ) : null}
                        {showVatNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <VatNumber />
                            </GridItem>
                        ) : null}
                        {showTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <PaymentTerms />
                            </GridItem>
                        ) : null}
                        {/* Store Custom Code - Start */}
                        {order.requestedDeliveryDateDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <RequestedDate />
                            </GridItem>
                        ) : null}
                        {order.shipViaDescription || order.shipCode ? (
                            <GridItem {...styles.infoGridItem}>
                                <ShippingMethod />
                            </GridItem>
                        ) : null}
                        {/* Store Custom Code - End */}
                        {order.vmiLocationName ? (
                            <GridItem {...styles.infoGridItem}>
                                <VmiLocation />
                            </GridItem>
                        ) : null}
                    </GridContainer>
                ) : null}
                {order.notes ? (
                    <GridContainer {...styles.infoGridContainer}>
                        <GridItem {...styles.notesGridItem}>
                            <Notes />
                        </GridItem>
                    </GridContainer>
                ) : null}
            </Block>
            {/* Store Custom Code - Start */}
            {order.properties.userProfileName || order.properties.userProfileUserName ? (
                <Block {...styles.block}>
                    <Typography {...styles.billingInformationTitle}>{translate("User Information")}</Typography>
                    <GridContainer {...styles.infoGridContainer}>
                        {order.properties.userProfileName && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("Username")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties.userProfileName}</Typography>
                            </GridItem>
                        )}
                        {order.properties.userProfileUserName && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.infoLabel}>{translate("Email")}</Typography>
                                <Typography {...styles.infoValue}>{order.properties.userProfileUserName}</Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                </Block>
            ) : null}

            <Block {...styles.block}>
                <GridContainer {...styles.adressesGridContainer} /* Store Custom Code */>
                    <GridItem {...styles.adressesGridItem} /* Store Custom Code */>
                        <Typography {...styles.billingInformationTitle}>{translate("Billing Information")}</Typography>
                        <BillingAddress />
                    </GridItem>
                    <GridItem {...styles.adressesGridItem} /* Store Custom Code */>
                        <Typography {...styles.shippingInformationTitle}>
                            {translate("Shipping Information")}
                        </Typography>
                        <GridContainer {...styles.infoGridContainer}>
                            <GridItem {...styles.shippingGridItem /* Store Custom Code */}>
                                <ShippingAddress />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </Block>
            {/* Store Custom Code - End */}
        </OrderStateContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Information",
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
    },
};

export default widgetModule;
